import { createWebHistory, createRouter } from 'vue-router'
import { watch } from 'vue'
import appConfig from '../../app.config'
import store from '@/state/store'
import i18n from '@/i18n'

import coreRoutes from './routes/core'
import companyRoutes from './routes/company'
import samoRoutes from './routes/samo'
import managerRoutes from './routes/manager'
import errorRoutes from './routes/errors'

const router = createRouter({
    history: createWebHistory(),
    // createWebHashHistory()
    routes: [
        ...coreRoutes,
        ...companyRoutes,
        ...samoRoutes,
        ...managerRoutes,
        ...errorRoutes
    ],
    scrollBehavior() {
        return { top: 0, left: 0 }
    },
})

function normalizeCompany(c) {
    return {
        ...c,
        blocked:     c.blocked     ?? c.Block     ?? c.block ?? false,
        deleted:     c.deleted     ?? c.Deleted   ?? false,
        completed:   c.completed   ?? c.Completed ?? false,
        moderation:  c.moderation  ?? c.Moderation,
        internalError: c.internalError ?? c.InternalError ?? false,
        name: c.name ?? c.CompanyName ?? c.companyName,
    }
}

router.beforeEach(async (to, from, next) => {
    if (to.name === 'logout') {
        const keep = ['layout', 'lang']
        const keepLocal = Object.fromEntries(
            keep.map(k => [k, localStorage.getItem(k)]).filter(([, v]) => v != null)
        )
        const keepSession = Object.fromEntries(
            keep.map(k => [k, sessionStorage.getItem(k)]).filter(([, v]) => v != null)
        )

        localStorage.clear()
        Object.entries(keepLocal).forEach(([k, v]) => localStorage.setItem(k, v))
        sessionStorage.clear()
        Object.entries(keepSession).forEach(([k, v]) => sessionStorage.setItem(k, v))

        return next()
    }

    const isAuth = !!localStorage.getItem('jwt')
    const access = to.meta.access
    const isManagerRoute = to.path.startsWith('/manager')

    if (access === 'auth') {
        if (!isAuth) {
            return next({ name: 'login', query: { redirectFrom: to.fullPath } })
        }

        if (!store.state.auth.profileLoaded) {
            const ok = await store.dispatch('auth/fetchProfile', { force: false })
            if (!ok) {
                localStorage.removeItem('jwt')
                localStorage.removeItem('company')
                return next({ name: 'login', query: { redirectFrom: to.fullPath } })
            }
        }

        const user = store.state.auth.user || {}
        const rawUserType = user.UserType ?? user.userType ?? '0'
        const userTypeNum = Number(rawUserType)
        const isManagerOrAdmin = (userTypeNum === 1 || userTypeNum === 2)

        const allCompaniesRaw = store.state.company.companies || []
        const allCompanies = allCompaniesRaw.map(normalizeCompany)
        const hasCompanies = allCompanies.length > 0

        if (isManagerOrAdmin && !hasCompanies && !isManagerRoute) {
            return next({ name: 'ManagerDashboard' })
        }

        if (isManagerRoute) {
            if (!isManagerOrAdmin) {
                return next({ name: 'dashboard' })
            }
            return next()
        }

        const currentUuid = localStorage.getItem('company')
        const activeRaw = allCompanies.find(c => c.CompanyUUID === currentUuid) || allCompanies[0]
        const active = activeRaw ? normalizeCompany(activeRaw) : null

        if (active && (active.blocked || active.deleted || active.internalError)) {
            if (to.meta.allowIfCompanyError === true) {
                return next()
            }
            if (active.blocked)       return next({ name: 'ErrorBlocked' })
            if (active.deleted)       return next({ name: 'ErrorDeleted' })
            if (active.internalError) return next({ name: 'ErrorInternal' })
        }

        if (active && active.moderation !== true) {
            if (to.name === 'ErrorModeration') return next()
            return next({ name: 'ErrorModeration' })
        }

        const completed = !!active?.completed
        if (completed && to.name === 'CompanySetup') {
            return next({ name: 'dashboard' })
        }
        if (!completed && to.name !== 'CompanySetup' && to.meta.allowIfCompanyError !== true) {
            return next({ name: 'CompanySetup' })
        }

        if (to.meta.allowIfCompanyError !== true) {
            const permsMap = store.state.permissions.map || {}
            if (Object.keys(permsMap).length === 0) {
                try {
                    await store.dispatch('permissions/fetchPermissionsMap')
                } catch (e) {
                    localStorage.removeItem('jwt')
                    return next({ name: 'login' })
                }
            }

            const required = to.meta.permissions
            if (Array.isArray(required) && required.length > 0) {
                const mapObj = store.state.permissions.map || {}
                const grantedIds = new Set((active?.permissions || []).map(String))
                const grantedNames = new Set(
                    (active?.permissions || [])
                        .map(id => mapObj[id])
                        .filter(Boolean)
                )

                const GUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

                const hasAll = required.every(req => {
                    if (typeof req !== 'string' || !req.trim()) return false
                    return GUID_RE.test(req) ? grantedIds.has(req) : grantedNames.has(req)
                })

                if (!hasAll) {
                    return next({ name: 'dashboard' })
                }
            }
        }
        return next()
    }
    return next()
})

router.afterEach(() => {
    // 1. Синхронизируем i18n с localStorage при переходах
    const activeLang = localStorage.getItem('lang') || 'en'
    if (i18n.global.locale.value) {
        if (i18n.global.locale.value !== activeLang) i18n.global.locale.value = activeLang
    } else {
        if (i18n.global.locale !== activeLang) i18n.global.locale = activeLang
    }
})

// 2. Реактивный watch следит за сменой роута и подгрузкой локали
watch(
    () => [
        router.currentRoute.value.meta?.title,
        i18n.global.locale.value || i18n.global.locale
    ],
    ([metaTitle]) => {
        let baseTitle = appConfig.title
        const currentPath = router.currentRoute.value.path || ''
        const isManagerRoute = currentPath.startsWith('/manager')

        if (!isManagerRoute) {
            const companiesRaw = store.state.company.companies || []
            const companies = companiesRaw.map(normalizeCompany)
            const uuid = localStorage.getItem('company')
            const current = companies.find(c => c.CompanyUUID === uuid) || companies[0]
            if (current?.name) {
                baseTitle = current.name
            }
        }

        let pageTitle = metaTitle
        if (pageTitle) {
            // Как только i18n загрузит словари или сменит язык, watch дернет t()
            pageTitle = i18n.global.t(pageTitle)
        }

        document.title = pageTitle ? `${pageTitle} | ${baseTitle}` : baseTitle
    },
    { immediate: true }
)

export default router